import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Landmark, Pencil, Plus, RotateCw, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import Config from "src/Config";
import { GD } from "src/GD";
import { notify } from "src/tools/notify";
import { FormParser } from "../FormParser";
import { S_CONFIRM } from "../popup/general/ConfirmDialog";
import { compareText, formatIban, matchesQuery, plural } from "../providers/providersData";
import {
	Cell,
	Chip,
	CopyButton,
	DialogBody,
	DirectoryCard,
	DirectoryRow,
	embeddedFormSx,
	EmptyState,
	EntityCell,
	IconButton,
	IconTile,
	Mono,
	Muted,
	Notice,
	ProviderDialog,
	SearchField,
	SkeletonRows,
	TabIntro,
	TabSurface,
} from "../providers/providersUi";
import { AdminButton } from "./administration/AdminControls";

type TBeneficiary = Record<string, any> & {
	id?: number;
	status?: string;
	name?: string;
	iban?: string;
	bic?: string;
	bank?: string;
	country?: string;
	city?: string;
	raw?: string;
};

const getObBeneficiaryUrl = (method: "list" | "status" | "set" | "get") => {
	const baseUrl = window.location.hostname === "localhost" ? Config.scheme.URL_API : window.location.origin + "/api/v2/";
	const apiUrl = new URL(baseUrl);
	apiUrl.pathname = "/api/v2/ob/beneficiary/" + method;
	return apiUrl.toString();
};

const normalizeBeneficiary = (beneficiary: TBeneficiary): TBeneficiary => {
	if (!beneficiary.raw || typeof beneficiary.raw !== "string") return beneficiary;

	try {
		const raw = JSON.parse(beneficiary.raw);
		if (!raw || typeof raw !== "object") return beneficiary;
		return {
			...raw,
			...beneficiary,
		};
	} catch (_e) {
		return beneficiary;
	}
};

const normalizeBeneficiaries = (response: IAPIResponse): TBeneficiary[] => {
	let beneficiaries: TBeneficiary[] = [];
	if (!response.data) return beneficiaries;
	if (Array.isArray(response.data)) beneficiaries = response.data;
	if (Array.isArray((response.data as any).data)) beneficiaries = (response.data as any).data;
	if (beneficiaries.length > 0) return beneficiaries.map(normalizeBeneficiary);
	return [];
};

const isFormEmpty = (response: IAPIResponse): boolean => {
	if (response.error) return false;
	const data = response.data;
	if (!data || typeof data !== "object") return true;

	const formData = (data as any).data ?? data;
	if (!formData || typeof formData !== "object") return true;

	return Object.keys(formData).filter(key => !key.startsWith("$__")).length === 0;
};

const normalizeFormResponse = (response: IAPIResponse): IAPIResponse => {
	if (response.error || !response.data || typeof response.data !== "object") return response;

	const data = response.data as Record<string, any>;
	const candidates = [data.data, data.form, data.schema, data.model, data.data?.form, data.data?.schema, data.data?.model];

	for (const candidate of candidates) {
		if (!candidate || typeof candidate !== "object") continue;
		const fields = Object.keys(candidate).filter(key => !key.startsWith("$__"));
		if (fields.length > 0) {
			return {
				data: candidate,
			};
		}
	}

	return response;
};

/** Bank accounts that receive open-banking payments; the server lists active ones only. */
export const AcquirerBeneficiaries = () => {
	useLocale();
	const [beneficiaries, setBeneficiaries] = useState<TBeneficiary[] | null>(null);
	const [error, setError] = useState<IError | null>(null);
	const [busy, setBusy] = useState(false);
	const [editor, setEditor] = useState<{ id?: number } | null>(null);
	const [query, setQuery] = useState("");

	const loadBeneficiaries = async () => {
		setBusy(true);
		setError(null);

		const response = await GD.REQ_API.request({
			method: "ob/beneficiary/list",
			url: getObBeneficiaryUrl("list"),
			data: {},
		});

		if (response.error) {
			setError(response.error);
			setBeneficiaries([]);
		} else {
			setBeneficiaries(normalizeBeneficiaries(response).filter(beneficiary => beneficiary.status !== "deleted"));
		}

		setBusy(false);
	};

	useEffect(() => {
		loadBeneficiaries();
	}, []);

	const remove = (beneficiary: TBeneficiary) => {
		if (!beneficiary.id) return;
		S_CONFIRM.invoke({
			title: msg("deleteBeneficiary"),
			message: msg("templateDeleteChannelsLinkedToItStopPayingOutToThisAccount_767994d1", { v0: beneficiary.name ?? beneficiary.id }),
			confirm: "Delete",
			onResult: async value => {
				if (value !== "confirm") return;
				setBusy(true);
				setError(null);
				const response = await GD.REQ_API.request({
					method: "ob/beneficiary/status",
					url: getObBeneficiaryUrl("status"),
					data: { id: beneficiary.id, status: "deleted" },
				});
				if (response.error) {
					setError(response.error);
					setBusy(false);
					return;
				}
				notify.success(msg("beneficiaryDeleted"));
				await loadBeneficiaries();
			},
		});
	};

	const all = beneficiaries ?? [];
	const visible = all
		.filter(item => matchesQuery(query, [item.name, item.iban, item.iban?.replace(/\s+/g, ""), item.bic, item.bank, item.country, item.city, item.id]))
		.sort((a, b) => compareText(a.name, b.name));
	const addButton = (
		<AdminButton variant="primarySmall" size="small" disabled={busy} onClick={() => setEditor({})}>
			<Plus size={14} />{msg("addBeneficiary")}</AdminButton>
	);

	return (
		<TabSurface>
			<TabIntro
				text={msg("bankAccountsThatReceiveOpenBankingPaymentsLinkABeneficiaryTo_ba298aba")}
				actions={
					<>
						<AdminButton variant="secondarySmall" size="small" disabled={busy} onClick={() => void loadBeneficiaries()}>
							<RotateCw size={14} />{msg("refresh")}</AdminButton>
						{addButton}
					</>
				}
			/>
			{error && <Notice tone="error">{error.message}</Notice>}
			<DirectoryCard
				columns="minmax(240px, 1fr) minmax(200px, max-content) minmax(90px, max-content) minmax(110px, max-content) minmax(64px, max-content) max-content"
				head={["Beneficiary", "IBAN", "BIC", "Bank", "Country", ""]}
				toolbar={<SearchField value={query} onChange={setQuery} placeholder={msg("searchByNameIbanBicOrBank")} />}
				footer={beneficiaries && all.length > 0 && <span>{query ? msg("templateOf_64f93023", { v0: visible.length, v1: plural(all.length, "beneficiary", "beneficiaries") }) : plural(all.length, "beneficiary", "beneficiaries")}</span>}>
				{beneficiaries === null ? (
					<SkeletonRows />
				) : !all.length ? (
					!error && <EmptyState icon={<Landmark size={20} />} title={msg("noBeneficiariesYet")} text={msg("addTheBankAccountThatShouldReceiveOpenBankingPayments")} action={addButton} />
				) : !visible.length ? (
					<EmptyState icon={<Landmark size={20} />} title={msg("nothingMatchesTheSearch")} text={msg("tryAShorterQuery")} />
				) : (
					visible.map(beneficiary => (
						<DirectoryRow key={beneficiary.id} label={msg("templateEdit_f1be7e63", { v0: beneficiary.name ?? beneficiary.id })} onOpen={() => setEditor({ id: beneficiary.id })}>
							<Cell kind="main">
								<EntityCell
									icon={
										<IconTile>
											<Landmark />
										</IconTile>
									}
									name={beneficiary.name || msg("templateBeneficiary_74818786", { v0: beneficiary.id })}
									sub={
										<>
											<Mono>#{beneficiary.id}</Mono>
											{beneficiary.city && <span>{beneficiary.city}</span>}
										</>
									}
								/>
							</Cell>
							<Cell label={msg("iban")}>
								{beneficiary.iban ? (
									<span style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}>
										<Mono>{formatIban(beneficiary.iban)}</Mono>
										<CopyButton value={beneficiary.iban.replace(/\s+/g, "")} label={msg("copyIban")} />
									</span>
								) : (
									<Muted>—</Muted>
								)}
							</Cell>
							<Cell label="BIC">{beneficiary.bic ? <Mono>{beneficiary.bic}</Mono> : <Muted>—</Muted>}</Cell>
							<Cell label={msg("bank")}>{beneficiary.bank || <Muted>—</Muted>}</Cell>
							<Cell label={msg("country")}>{beneficiary.country ? <Chip>{String(beneficiary.country).toUpperCase()}</Chip> : <Muted>—</Muted>}</Cell>
							<Cell kind="trail">
								<IconButton label={msg("editBeneficiary")} tone="accent" onClick={() => setEditor({ id: beneficiary.id })}>
									<Pencil size={15} />
								</IconButton>
								<IconButton label={msg("deleteBeneficiary")} tone="danger" disabled={busy} onClick={() => remove(beneficiary)}>
									<Trash2 size={15} />
								</IconButton>
							</Cell>
						</DirectoryRow>
					))
				)}
			</DirectoryCard>

			{editor && (
				<BeneficiaryEditor
					id={editor.id}
					onClose={() => setEditor(null)}
					onSaved={() => {
						setEditor(null);
						notify.success(editor.id ? msg("beneficiarySaved") : msg("beneficiaryCreated"));
						loadBeneficiaries();
					}}
				/>
			)}
		</TabSurface>
	);
};

const BeneficiaryEditor = ({ id, onClose, onSaved }: { id?: number; onClose: () => void; onSaved: () => void }) => {
	useLocale();
	return (
		<ProviderDialog width={640} eyebrow={msg("openBankingBeneficiary")} title={id ? msg("editBeneficiary") : msg("newBeneficiary")} onClose={onClose}>
			<DialogBody>
				<FormParser
					appearance="modern"
					sx={embeddedFormSx}
					size="small"
					secondaryButton={null}
					onSubmit={async (data: any): Promise<any> => {
						const payload = id && data && typeof data === "object" && !("id" in data) ? { ...data, id } : data;
						const response = await GD.REQ_API.request({
							method: "ob/beneficiary/set",
							url: getObBeneficiaryUrl("set"),
							data: payload,
						});
						if (!response.error) onSaved();
						return response;
					}}
					onRequest={async (): Promise<any> => {
						const response = normalizeFormResponse(
							await GD.REQ_API.request({
								method: "ob/beneficiary/get",
								url: getObBeneficiaryUrl("get"),
								...(id ? { data: { id } } : {}),
							}),
						);

						if (id || !isFormEmpty(response)) return response;

						return normalizeFormResponse(
							await GD.REQ_API.request({
								method: "ob/beneficiary/get",
								url: getObBeneficiaryUrl("get"),
								data: { id: 0 },
							}),
						);
					}}
				/>
			</DialogBody>
		</ProviderDialog>
	);
};
